<template>
  <parent>
    <div class="d-flex flex-column h-100">
      <div class="container">
        <div class="d-flex w-100 mb-2">
          <b-button
            variant="bg-white"
            :to="{
              name: 'object-files',
              params: {
                id: bucketName,
              },
            }"
          >
            <GjIcon
              class="icon-left"
              name="ArrowLeft"
            />
            Files
          </b-button>
        </div>
      </div>
      <template v-if="loading">
        <div class="d-flex justify-content-center align-items-center flex-grow-1">
          <b-spinner
            variant="primary"
            label="Text Centered"
          />
        </div>
      </template>

      <template v-else-if="serverDown">
        There is something wrong in our side. Please try again later.
      </template>

      <paper
        v-else
        class="container"
      >
        <template v-slot:title>
          <div class="d-flex">
            <span class="mr-1">Region: <span>{{ bucket.region || 'EU West' }}</span></span>
            <!-- <span>Creation date: <span>May 14, 2021 - 09:41:04 AM</span></span> -->
          </div>
        </template>

        <div
          class="position-relative"
          :class="{ filter: savingChanges }"
        >
          <button-collapse
            title="File Listing"
            :collapse="hideFileListing"
            @show="hideFileListing = false"
            @cancel="onCancel('fileListing')"
            @save="onSave('fileListing')"
          >
            <template #subtitle>
              <span class="subtitle">
                {{ bucket.fileListing ? 'Enabled. ' : 'Restricted. ' }}
                <span class="text-secondary">
                  {{
                    bucket.fileListing
                      ? 'Anyone can list the content of this bucket. '
                      : 'Only users who connect to this Space using access keys can list the contents.'
                  }}
                </span>
              </span>
            </template>

            <template #body>
              <div class="col-md-8 col-lg-6 p-0">
                <custom-input-radio
                  v-model="fileListing"
                  label="Choose file listing type"
                  :options="fileListingOptions"
                />
                <custom-alert icon-name="Info">
                  <strong>Important:</strong>
                  This setting has no effect on whether individual files are
                  visible. It only determines if anonymous users can list the
                  name, size, and other metadata for the files in this Space.
                </custom-alert>
              </div>
            </template>
          </button-collapse>

          <button-collapse
            title="Bucket Versioning"
            :save="!objectLock"
            :collapse="hideBucketVersioning"
            @show="hideBucketVersioning = false"
            @cancel="onCancel('bucketVersioning')"
            @save="onSave('bucketVersioning')"
          >
            <template #subtitle>
              <template v-if="objectLock">
                <span class="subtitle">Enabled.
                  <span class="text-secondary">
                    Enabled object lock automatically enables Bucket
                    Versioning.
                  </span>
                </span>
              </template>
              <template v-else>
                <span class="subtitle">
                  {{ bucket.version ? 'Enabled. ' : 'Disabled. ' }}
                  <span class="text-secondary">
                    Bucket verisoning is {{ bucket.version ? 'enabled. ' : 'disabled. ' }}
                  </span>
                </span>
              </template>
            </template>

            <template #body>
              <div class="col-md-8 col-lg-6 p-0">
                <custom-input-radio
                  v-model="bucketVersioning"
                  label="Bucket Versioning"
                  :options="bucketVersioningOptions"
                />

                <custom-alert icon-name="Info">
                  Versioning is a means of keeping multiple variants of an
                  object in the same bucket. You can use versioning to preserve,
                  retrieve, and restore every version of every object stored in
                  your bucket. With versioning, you can easily recover from both
                  unintended user actions and application failures
                </custom-alert>
              </div>
            </template>
          </button-collapse>

          <!-- <button-collapse
          title="Object Lock"
          :save="false"
        >

          <template #subtitle>
            <span class="subtitle">{{ objectLock ? 'Enabled: ' : 'Disabled: ' }}<span class="text-secondary"> Locking is not supported after the object is created</span></span>
          </template>

          <template #body>
            <div class="col-md-8 col-lg-6 p-0">
              <custom-input-radio
                v-model="objectLock"
                label="Object Lock"
                :options="objectLockOptions"
              />
              <custom-alert
                icon-name="InfoAlt"
                icon-color="#e34850"
              >
                Object Lock works only in versioned objects. Enabling object lock automatically enables Bucket Versioning.
              </custom-alert>
              <custom-alert icon-name="Info">
                Store objects using a write-once-read-many (WORM) model to help you prevent objects from being deleted or overwritten for a fixed amount of time or indefinitely.
              </custom-alert>
            </div>
          </template>

        </button-collapse> -->

          <button-collapse
            v-if="['Amazon', 'DigitalOcean', 'CDN77'].includes(externalAccount.providerName) || externalAccount.providerName === null"
            :collapse="hideCors"
            title="CORS Settings"
            cancel-title="Close"
            :save="false"
            @show="hideCors = false"
            @cancel="hideCors = true"
          >
            <template #subtitle>
              <span class="subtitle"><span class="text-secondary">Add, update or remove origins</span></span>
            </template>

            <template #body>
              <cors :account-id="accountId" />
            </template>
          </button-collapse>
          <button-collapse
            title="CDN (Content Delivery Network)"
            :collapse="hideCdn"
            @show="hideCdn = false"
            @cancel="onCancel('enableCdn')"
            @save="onSave('enableCdn')"
          >
            <template #subtitle>
              <span class="subtitle">
                {{ bucket.enableCdn ? 'Enabled.' : 'Disabled' }}
                <span
                  v-if="bucket.enableCdn"
                  class="text-secondary"
                >
                  {{ bucket.cdnEndpoint }}
                </span>
              </span>
            </template>

            <template #body>
              <div class="col-md-8 col-lg-6 p-0">
                <custom-input-radio
                  v-model="enableCdn"
                  label="CDN"
                  :options="enableCdnOptions"
                />
                <custom-alert icon-name="Info">
                  A CDN is a distributed group of servers in a data center
                  called Point of Presence, that provide fast delivery of web
                  content.
                </custom-alert>
              </div>
            </template>
          </button-collapse>

          <button-collapse
            v-if="externalAccount.providerName === null || externalAccount.providerName === 'Amazon'"
            title="Tags"
            :collapse="hideTags"
            @show="hideTags = false"
            @save="validation()"
            @cancel="cancelTags()"
          >
            <loading-page v-if="fetchingTags" />
            <template
              v-if="!fetchingTags"
              #subtitle
            >
              <span class="text-secondary">{{
                bucketTags.length === 0
                  ? 'There are no tags created for this bucket'
                  : 'Add, update or remove tags'
              }}</span>
            </template>
            <template
              v-else
              #subtitle
            >
              <GjIcon
                class="loader"
                size="20"
                name="Loader"
              />
            </template>

            <template
              v-if="!fetchingTags"
              #body
            >
              <div class="col-12 p-0">
                <validation-observer ref="rules">
                  <tags
                    v-if="!hideTags"
                    details
                    @updateTags="updateTags"
                  />
                </validation-observer>
              </div>
            </template>
          </button-collapse>

          <button-collapse
            title="Lifecycle"
            :collapse="hideLifecycle"
            :save="false"
            @show="hideLifecycle = false"
            @cancel="hideLifecycle = true"
          >
            <template #subtitle>
              <span class="subtitle"><span class="text-secondary">Add, update or remove lifecycles</span></span>
            </template>
            <template #body>
              <lifecycle />
            </template>
          </button-collapse>

          <div class="delete d-flex flex-column flex-md-row">
            <div class="d-flex flex-column mr-auto">
              <p class="title">
                Delete this bucket
              </p>
              <span class="subtitle text-secondary">This will schedule your bucket to be deleted. Your bucket and
                all associated objects will be permanently destroyed, scrubbed,
                and be irretrievable after the deletion period. You will NOT be
                billed for the Bucket from the time you requested a delete to
                the time the Bucket and its contents are actually deleted.</span>
            </div>
            <div class="mt-1 mt-md-0 ml-auto">
              <b-button
                variant="outline-danger"
                class="ml-1"
                @click="openDelete = true"
              >
                <GjIcon
                  name="Delete"
                  class="icon-left"
                />
                Delete
              </b-button>
            </div>
          </div>

          <template v-if="savingChanges">
            <div class="loading">
              <b-spinner
                variant="primary"
                label="Text Centered"
              />
            </div>
          </template>
        </div>

        <delete-confirmation
          :show="openDelete"
          @close="openDelete = false"
          @delete="onDelete()"
        />
      </paper>
    </div>
  </parent>
</template>
<script>
import Paper from '@/components/paper/Paper.vue'
import ButtonCollapse from '@/components/button/ButtonCollapse.vue'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import { ValidationObserver } from 'vee-validate'
import Tags from '../../buckets/bucket-form/Tags.vue'
import DeleteConfirmation from './DeleteConfirmation.vue'

import Cors from './cors/Cors.vue'
import Lifecycle from './lifecycle/Lifecycle.vue'
import redirectError from '@/services/redirectError'

export default {
  name: 'Files',
  components: {
    Paper,
    ButtonCollapse,
    CustomInputRadio,
    Tags,
    DeleteConfirmation,
    Cors,
    ValidationObserver,
    Lifecycle,
  },
  data() {
    return {
      openDelete: false,
      bucketId: null,
      fileListing: false,
      fileListingOptions: [
        {
          text: 'Restrict File Listing',
          value: false,
          description: 'Only users with the access key can list the contents',
        },
        {
          text: 'Enable File Listing',
          value: true,
          description: 'Anyone can list the content of this bucket',
        },
      ],
      bucketVersioning: true,
      bucketVersioningOptions: [],
      objectLock: false,
      objectLockOptions: [],
      corsSettings: true,
      serverDown: false,
      hideFileListing: true,
      hideTags: true,
      hideCors: true,
      hideLifecycle: true,
      hideBucketVersioning: true,
      hideCdn: true,
      enableCdn: false,
      enableCdnOptions: [
        { text: 'Enable', value: true },
        { text: 'Disable', value: false },
      ],
      accountId: 0,
      newTags: [],
    }
  },
  computed: {
    loading() {
      return this.$store.state.accountBuckets.gettingBucket
    },
    savingChanges() {
      return (
        this.$store.state.accountBuckets.updatingBucket
        || this.$store.state.accountBuckets.deletingBucket
      )
    },
    bucket() {
      return this.$store.state.accountBuckets.bucket
    },
    fetchingTags() {
      return this.$store.state.accountBuckets.fetchingTags
    },
    bucketTags() {
      return this.$store.state.accountBuckets.bucketTags
    },
    bucketName() {
      return this.$route.params.id
    },
    externalAccount() {
      return this.$store.state.externalAccount.externalAccount
    },
  },
  created() {
    this.accountId = this.$route.params.accountId
    this.bucketId = this.$route.params.id
    this.initializeBucket()
    this.fetchAccount()
  },
  beforeDestroy() {
    this.$store.commit('accountBuckets/UPDATE_TAGS', [])
  },
  methods: {
    initializeBucket() {
      this.$store
        .dispatch('accountBuckets/getBucket', {
          accountId: this.accountId,
          name: this.bucketId,
        })
        .then(() => {
          this.fileListing = this.bucket.fileListing
          this.objectLock = this.bucket.objectLock
          this.bucketVersioning = this.bucket.version

          this.bucketVersioningOptions.push(
            { text: 'Enabled', value: true },
            { text: 'Disabled', value: false, disabled: this.objectLock },
          )
          this.objectLockOptions.push(
            { text: 'Enabled', value: true, disabled: !this.objectLock },
            { text: 'Disabled', value: false, disabled: this.objectLock },
          )
          this.enableCdn = this.bucket.enableCdn
        })
        .catch(error => {
          if (error.response?.status === 424) {
            this.$router.push({ name: 'buckets-start' })
            return
          }
          redirectError(error)
          this.errorToast(
            error.response?.data?.errors[0] || 'Something went wrong.',
          )
        })
    },
    fetchAccount() {
      this.$store
        .dispatch('externalAccount/fetchAccount', this.$route.params.accountId)
        .then(() => {
          this.getTags()
        })
    },
    getTags() {
      if (this.externalAccount.accountType !== 0 && this.externalAccount.providerName !== 'Amazon') return

      this.$store
        .dispatch('accountBuckets/getBucketTags', {
          accountId: this.accountId,
          name: {
            bucketId: this.bucketId,
          },
        })
        .catch(error => {
          if (error.response.status !== 404) {
            this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching bucket tags.')
          }
        })
    },
    onCancel(val) {
      // if (val === 'objectLock') {
      //   this.objectLock = this.bucket.objectLock
      // } else
      if (val === 'fileListing') {
        this.fileListing = this.bucket.fileListing
        this.hideFileListing = true
      } else if (val === 'bucketVersioning') {
        this.bucketVersioning = this.bucket.version
        this.hideBucketVersioning = true
      }
      if (val === 'enableCdn') {
        this.enableCdn = this.bucket.enableCdn
        this.hideCdn = true
      }
    },
    onSave(val) {
      this.$store
        .dispatch('accountBuckets/updateBucket', {
          accountId: this.accountId,
          query: {

            fileListing:
              val === 'fileListing' ? this.fileListing : this.bucket.fileListing,
            bucketName: this.bucket.name,
            objectLock: this.bucket.objectLock,
            version:
              val === 'bucketVersioning'
                ? this.bucketVersioning
                : this.bucket.version,
            enableCdn:
              val === 'enableCdn' ? this.enableCdn : this.bucket.enableCdn,
          },
        })
        .then(() => {
          this.successToast('Bucket was updated')
          this.updateBucket(val)
          if (val === 'fileListing') {
            this.hideFileListing = true
          } else if (val === 'bucketVersioning') {
            this.hideBucketVersioning = true
          } else if (val === 'enableCdn') {
            this.hideCdn = true
          }
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating bucket.')
        })
    },
    validation() {
      return new Promise((resolve, reject) => {
        this.$refs.rules.validate().then(success => {
          if (success) {
            resolve(true)
            this.submitTags()
            this.hideTags = true
          } else {
            reject()
          }
        })
      })
    },
    cancelTags() {
      this.$store.commit('accountBuckets/SET_TAGS', this.bucketTags)
      this.hideTags = true
    },
    updateTags(val) {
      this.newTags = val
    },
    submitTags() {
      const tags = this.formatTags()

      let endpoint = 'updateBucketTags'

      const query = {
        accountId: this.accountId,
        name: this.bucketName,
        tags,
        deleteAll: false,
      }

      if (tags.length === 0) {
        query.deleteAll = true
        endpoint = 'deleteBucketTag'
      }

      this.$store
        .dispatch(`accountBuckets/${endpoint}`, query)
        .then(() => {
          this.successToast('Tags were updated')
          this.hideTags = true
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating bucket tags.')
        })
    },
    formatTags() {
      const tags = []

      const innerTags = this.newTags.filter(
        (v, i, a) => a.findIndex(
          t => t.tagKey === v.tagKey && t.tagKey !== '' && t.tagValue !== '',
        ) === i,
      )

      for (let i = 0; i < innerTags.length; i++) {
        tags.push({
          key: innerTags[i].tagKey,
          value: innerTags[i].tagValue,
        })
      }

      return tags
    },
    updateBucket(val) {
      if (val === 'objectLock') {
        this.bucket.objectLock = this.objectLock
      } else if (val === 'fileListing') {
        this.bucket.fileListing = this.fileListing
      } else if (val === 'bucketVersioning') {
        this.bucket.version = this.bucketVersioning
      }
      if (val === 'enableCdn') {
        this.bucket.enableCdn = this.enableCdn
      }
    },
    onDelete() {
      this.$store
        .dispatch('accountBuckets/deleteBucket', { accountId: this.accountId, bucket: { name: this.bucketName } })
        .then(() => {
          this.successToast('Bucket was deleted')
          this.openDelete = false
          this.$router.push({ name: 'buckets' })
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting bucket.')
        })
    },
  },
}
</script>
<style lang="scss" scoped>
.delete {
  margin: 0 24px 24px;

  .title {
    font-weight: 500;
  }

  .subtitle {
    font-size: 12px;
  }
}

.loading {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  top: 0;
}

.filter {
  filter: blur(1px);
}

.loader {
  -webkit-animation: spin 3s linear infinite;
  -moz-animation: spin 3s linear infinite;
  animation: spin 3s linear infinite;
}

@-moz-keyframes spin {
  100% {
    -moz-transform: rotate(360deg);
  }
}

@-webkit-keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
